/** @format */

/**
 * Centralized React Query cache configuration
 *
 * ## Philosophy: Stale-While-Revalidate
 *
 * Cache exists to cover request latency, NOT to avoid making requests.
 * Every cache hit should be followed by a background fetch to refresh the data.
 * The user sees cached data instantly while fresh data loads behind the scenes.
 *
 * 1. **WebSocket-updated data**: staleTime 0 (always refetch on mount)
 *    - Cached data displayed immediately, background fetch verifies freshness
 *    - WebSocket pushes ongoing updates after initial fetch
 *    - Examples: run details, vehicles, laps, pit stops, driver results
 *
 * 2. **Polled data**: Short staleTime < refetchInterval
 *    - Frontend polls at regular intervals for updates
 *    - staleTime must be less than refetchInterval to prevent serving stale cached data
 *    - Examples: weather data (not on WebSocket)
 *
 * 3. **Static/rare-change data**: 30 minute staleTime
 *    - Data that rarely changes within a session
 *    - Still refetches after IndexedDB hydration (via onSuccess invalidation)
 *    - Examples: driver lists, track info, app config
 *
 * 4. **Playback/historical data**: Infinity staleTime
 *    - The ONLY tier where avoiding requests is correct
 *    - Historical run data is immutable — once fetched, it never changes
 *    - Cached indefinitely for timeline scrubbing performance
 *
 * 5. **Route loader prefetch data**: 5 minutes staleTime
 *    - Data prefetched by route loaders for instant rendering
 *    - Hooks will reactively update if data becomes stale
 */

export const QUERY_CONFIG = {
  /**
   * WebSocket-updated resources (run details, vehicles, laps, pit stops, etc.)
   *
   * staleTime: 5s covers the initial mount cascade — widgets mounting within 5s of the
   * first fetch reuse cached data instead of triggering redundant HTTP requests.
   * WebSocket continues to push real-time updates after the initial fetch regardless.
   * refetchOnMount defaults to true (React Query default) — intentionally not overridden.
   */
  WEBSOCKET_DATA: {
    staleTime: 5 * 1000, // 5 seconds — prevents duplicate fetches during mount burst
    refetchInterval: false as const,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
    gcTime: 5 * 60 * 1000,
  },

  /**
   * Polled resources (weather, non-WebSocket live data)
   *
   * Frontend polls every 20s, staleTime < interval prevents cached data.
   * Used for data that changes frequently but isn't on WebSocket.
   */
  POLLED_LIVE_DATA: {
    staleTime: 15 * 1000, // 15 seconds (MUST be less than 20s poll interval)
    refetchInterval: 20 * 1000, // 20 seconds
    refetchIntervalInBackground: false,
    gcTime: 5 * 60 * 1000,
  },

  /**
   * Static/rare-change data (driver lists, track info, formats)
   *
   * Data that rarely changes within a session but should still verify
   * freshness after IndexedDB hydration across sessions.
   *
   * Note: PersistQueryClientProvider onSuccess (App.tsx) invalidates queries
   * matching this staleTime value to refresh static data across sessions.
   */
  STATIC_DATA: {
    staleTime: 30 * 60 * 1000, // 30 minutes — refetch after half an hour
    refetchInterval: false as const,
    refetchOnWindowFocus: false,
    gcTime: 10 * 60 * 1000, // Keep longer since it rarely changes
  },

  /**
   * Route loader prefetch data
   *
   * Data prefetched by TanStack Router loaders for instant rendering.
   * 5 minute staleTime allows hooks to reactively update if needed.
   */
  LOADER_PREFETCH: {
    staleTime: 5 * 60 * 1000, // 5 minutes
    // No refetch settings - loaders run once per navigation
  },

  /**
   * Playback mode data (historical runs, never changes)
   *
   * Historical run data that is immutable and should be cached indefinitely.
   * This is the ONLY tier where staleTime: Infinity is appropriate.
   * Longer gcTime for timeline scrubbing performance.
   */
  PLAYBACK_DATA: {
    staleTime: Infinity,
    refetchInterval: false as const,
    refetchOnMount: false as const,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
    gcTime: 10 * 60 * 1000, // Keep longer for timeline scrubbing
  },
} as const;

/**
 * Helper function to get appropriate config based on context
 *
 * @param isLive - Whether the data is for a live run or historical playback
 * @param hasWebSocket - Whether the data is updated via WebSocket
 * @returns Appropriate query configuration object
 */
export const getDataConfig = (isLive: boolean, hasWebSocket: boolean) => {
  if (!isLive) {
    return QUERY_CONFIG.PLAYBACK_DATA;
  }
  if (hasWebSocket) {
    return QUERY_CONFIG.WEBSOCKET_DATA;
  }
  return QUERY_CONFIG.POLLED_LIVE_DATA;
};
